/*
  Collection Color Swatches (Zara-Style)
  - Kein Slider mehr: feste Reihe + "+N" Chip (Liquid-seitig)
  - Klick auf Swatch aktualisiert Bild (mit Fade), Titel, Preis, Badges, Video-Thumbnail
  - Quellen: Metafield-Produkte (data-source="metafield") oder Varianten (data-source="variants")
*/

class CollectionColorSwatches {
    constructor(container) {
        this.container = container;
        this.productItem = container.closest('product-item');
        this.imageCache = new Map();

        this.swatches = Array.from(container.querySelectorAll('.collection-color-swatch[data-product-image]'));
        if (!this.swatches.length || !this.productItem) return;

        this.productElements = {
            image: this.productItem.querySelector('.product-item__primary-image'),
            title: this.productItem.querySelector('.product-item-meta__title'),
            priceList: this.productItem.querySelector('.price-list'),
            labelList: this.productItem.querySelector('.product-item__label-list'),
            videoThumbnail: this.productItem.querySelector('ed-video-thumbnail.ed-video-thumbnail--product-card')
        };

        this.container.addEventListener('click', (e) => {
            const swatch = e.target.closest('.collection-color-swatch[data-product-image]');
            if (swatch) this.handleSwatchClick(swatch);
        });

        this.fitRow();
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.fitRow());
            this.resizeObserver.observe(this.container);
        }

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    this.preloadImages();
                    observer.unobserve(this.container);
                }
            }, { rootMargin: '100px' });
            observer.observe(this.container);
        } else {
            this.preloadImages();
        }
    }

    preloadImages() {
        this.swatches.forEach(swatch => {
            const imageUrl = swatch.dataset.productImage;
            if (imageUrl && !this.imageCache.has(imageUrl)) {
                const img = new Image();
                img.src = imageUrl;
                /* decode() wärmt zusätzlich den Decode-Cache → Einblenden ruckelfrei */
                img.decode?.().catch(() => {});
                this.imageCache.set(imageUrl, img);
            }
        });
    }

    handleSwatchClick(swatch) {
        if (swatch.classList.contains('active')) return;

        this.swatches.forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');

        const imageUrl = swatch.dataset.productImage;
        if (imageUrl && this.productElements.image) {
            this.updateProductImage(imageUrl, swatch.dataset.variantUrl);
        }

        this.updateProductDetails(swatch.dataset);
        this.fitRow();
    }

    /* Eine Zeile: so viele Quadrate wie die Karte hergibt, Rest als +N. */
    fitRow() {
        const grid = this.container.querySelector('.collection-color-swatch-grid');
        if (!grid || !this.swatches.length) return;

        const more = grid.querySelector('.collection-color-swatch--more');
        const max = Math.max(1, parseInt(this.container.dataset.maxVisible, 10) || 12);
        const allowPlus = this.container.dataset.showPlus !== 'false';
        const items = this.swatches;

        items.forEach((el) => { el.hidden = false; });

        const width = grid.clientWidth;
        const size = items[0].getBoundingClientRect().width;
        if (width < 8 || size < 8) {
            if (more) more.hidden = true;
            return;
        }

        const gap = parseFloat(getComputedStyle(grid).columnGap || getComputedStyle(grid).gap) || 0;
        const slotsFor = (plusW) => {
            const usable = width - (plusW > 0 ? plusW + gap : 0);
            return Math.max(1, Math.floor((usable + gap) / (size + gap)));
        };

        let slots = Math.min(max, items.length, slotsFor(0));
        if (allowPlus && more && items.length > slots) {
            more.hidden = false;
            const chip = more.querySelector('.collection-color-swatch__more-chip');
            if (chip) chip.textContent = `+${items.length - 1}`;
            slots = Math.min(max, items.length, slotsFor(more.getBoundingClientRect().width));
        }

        const hiddenCount = items.length - slots;
        const activeIdx = items.findIndex((el) => el.classList.contains('active'));
        let start = 0;
        if (activeIdx >= slots) start = activeIdx - slots + 1;

        items.forEach((el, i) => {
            el.hidden = i < start || i >= start + slots;
        });

        if (more) {
            if (allowPlus && hiddenCount > 0) {
                more.hidden = false;
                const chip = more.querySelector('.collection-color-swatch__more-chip');
                if (chip) chip.textContent = `+${hiddenCount}`;
            } else {
                more.hidden = true;
            }
        }
    }

    updateProductImage(imageUrl, variantUrl) {
        const image = this.productElements.image;
        const imageContainer = image.closest('a');

        /* Laufende Wechsel-Token: Wer schnell mehrere Swatches klickt,
           gewinnt immer der letzte Klick */
        const token = (this.swapToken = (this.swapToken || 0) + 1);

        /* Doppel-Puffer-Crossfade: Das neue Bild wird als Overlay über das
           alte gelegt und erst eingeblendet, wenn es vollständig geladen UND
           dekodiert ist. Das alte Bild bleibt bis dahin sichtbar → kein Weiß. */
        const applySwap = () => {
            if (token !== this.swapToken) return;

            if (imageContainer && variantUrl) {
                imageContainer.href = variantUrl;
            }

            const overlay = image.cloneNode(false);
            overlay.removeAttribute('srcset');
            overlay.removeAttribute('sizes');
            overlay.removeAttribute('id');
            overlay.src = imageUrl;
            overlay.classList.add('swatch-image-overlay');

            image.parentNode.insertBefore(overlay, image.nextSibling);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    overlay.classList.add('is-visible');
                });
            });

            /* Nach dem Crossfade: Original-Bild auf neue Farbe stellen, Overlay weg */
            setTimeout(() => {
                if (token === this.swapToken) {
                    image.src = imageUrl;
                    image.srcset = '';
                }
                overlay.remove();

                /* Hover-Zweitbild gehört zur Original-Farbe → nach Farbwechsel entfernen.
                   Auch die --multiple-Klasse muss weg, sonst blendet der Hover-Effekt
                   das Hauptbild aus, obwohl kein Zweitbild mehr existiert. */
                const secondaryImage = this.productItem.querySelector('.product-item__secondary-image');
                if (secondaryImage) {
                    secondaryImage.remove();
                    this.productItem.querySelector('.product-item__image-wrapper--multiple')
                        ?.classList.remove('product-item__image-wrapper--multiple');
                }

                const swipe = this.productItem.querySelector('.product-item__swipe');
                if (swipe) {
                    swipe.scrollLeft = 0;
                    swipe.querySelectorAll('.product-item__swipe-slide:not(:first-child)').forEach((slide) => slide.remove());
                    swipe.classList.remove('product-item__swipe--multi');
                    /* --swipe bleibt. Die Klasse ist auf Mobil die Höhenbox
                       (aspect-ratio). Die Swipe-Spur liegt absolute darin.
                       Ohne die Klasse fällt das Foto auf 0. */
                }
            }, 280);
        };

        /* Bild garantiert fertig laden + dekodieren, dann erst tauschen */
        const loader = this.imageCache.get(imageUrl) || new Image();
        if (!loader.src) loader.src = imageUrl;
        this.imageCache.set(imageUrl, loader);

        if (loader.complete) {
            (loader.decode?.() || Promise.resolve()).catch(() => {}).then(applySwap);
        } else {
            loader.onload = () => (loader.decode?.() || Promise.resolve()).catch(() => {}).then(applySwap);
            loader.onerror = applySwap;
        }
    }

    updateProductDetails(data) {
        requestAnimationFrame(() => {
            if (data.productTitle && this.productElements.title) {
                this.productElements.title.textContent = data.productTitle;
                const titleLink = this.productElements.title.closest('a');
                if (titleLink && data.variantUrl) {
                    titleLink.href = data.variantUrl;
                }
            }

            if (data.productPrice && this.productElements.priceList) {
                this.productElements.priceList.innerHTML = this.getPriceHTML(data);
            }

            const isAvailable = data.available !== 'false';
            this.productItem.classList.toggle('product-item--sold-out', !isAvailable);
            this.retargetCard(data.variantUrl);
            this.syncSoldOutChip(isAvailable);

            let labelList = this.productElements.labelList;
            if (!labelList) {
                const imageWrapper = this.productItem.querySelector('.product-item__image-wrapper');
                if (imageWrapper) {
                    labelList = document.createElement('div');
                    labelList.className = 'product-item__label-list label-list';
                    imageWrapper.prepend(labelList);
                    this.productElements.labelList = labelList;
                }
            }
            if (labelList) {
                this.updateBadges(data, labelList, isAvailable);
            }

            this.updateVideoThumbnail(data);
        });
    }

    /* App: Tap retargets card + Plus to that colourway (sibling handle or ?variant=). */
    retargetCard(url) {
        if (!url) return;
        this.productItem.querySelectorAll('quick-buy-popover, quick-buy-drawer').forEach((el) => {
            el.setAttribute('href', url);
        });
        this.productItem.querySelectorAll('.collection-color-swatch--more').forEach((el) => {
            el.setAttribute('href', url);
        });
    }

    syncSoldOutChip(isAvailable) {
        if (!this.productItem.closest('.product-facet--app')) return;
        const imageWrapper = this.productItem.querySelector('.product-item__image-wrapper');
        if (!imageWrapper) return;
        let chip = this.productItem.querySelector('.product-item__sold-out-chip');
        if (!isAvailable) {
            if (!chip) {
                chip = document.createElement('span');
                chip.className = 'product-item__sold-out-chip';
                chip.textContent = window.themeVariables?.strings?.collectionSoldOut || 'Ausverkauft';
                imageWrapper.appendChild(chip);
            }
        } else if (chip) {
            chip.remove();
        }
    }

    updateBadges(data, labelList, isAvailable) {
        const appCard = !!this.productItem.closest('.product-facet--app');
        const appSale = !!this.productItem.closest('.product-facet--app-sale');
        const hasDiscount = data.comparePrice && data.comparePrice !== '';

        if (appCard && !isAvailable) {
            labelList.querySelectorAll('.label').forEach((el) => el.remove());
            return;
        }

        labelList.querySelectorAll('.label--custom, .label--custom2').forEach(el => el.remove());

        const badgeStr = data.productBadges || '';
        const customBadges = badgeStr ? badgeStr.split('|||') : [];
        customBadges.forEach((badge, index) => {
            const span = document.createElement('span');
            span.className = index === 0 ? 'label label--custom' : 'label label--custom2';
            span.textContent = badge;
            labelList.prepend(span);
        });

        let soldOutLabel = labelList.querySelector('.label--subdued');
        const discountLabel = labelList.querySelector('.label--highlight');

        if (!isAvailable && !appCard) {
            if (!soldOutLabel) {
                soldOutLabel = document.createElement('span');
                soldOutLabel.className = 'label label--subdued';
                soldOutLabel.textContent = window.themeVariables?.strings?.collectionSoldOut || 'Ausverkauft';
                labelList.appendChild(soldOutLabel);
            }
        } else if (soldOutLabel) {
            soldOutLabel.remove();
        }

        if (hasDiscount && isAvailable && (!appCard || appSale)) {
            const price = parseFloat((data.productPrice || '').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
            const compare = parseFloat((data.comparePrice || '').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
            if (compare > price && price > 0) {
                const savingsPercent = Math.round(((compare - price) / compare) * 100);
                const savingsText = window.themeVariables?.settings?.discountMode === 'saving'
                    ? data.comparePrice
                    : savingsPercent + '%';
                const discountText = appSale
                    ? (window.themeVariables?.strings?.collectionSale || 'Sale')
                    : (window.themeVariables?.strings?.collectionDiscount || '-@savings@')
                        .replace('@savings@', savingsText);
                if (discountLabel) {
                    discountLabel.textContent = discountText;
                } else {
                    const newLabel = document.createElement('span');
                    newLabel.className = 'label label--highlight';
                    newLabel.textContent = discountText;
                    labelList.appendChild(newLabel);
                }
            } else if (discountLabel) {
                discountLabel.remove();
            }
        } else if (discountLabel) {
            discountLabel.remove();
        }
    }

    updateVideoThumbnail(data) {
        const videoSrc = data.productVideo || '';
        const posterSrc = data.productVideoPoster || '';
        const imageWrapper = this.productItem.querySelector('.product-item__image-wrapper');
        let thumb = this.productElements.videoThumbnail;

        if (videoSrc) {
            if (thumb) {
                thumb.setAttribute('video-src', videoSrc);
                const posterImg = thumb.querySelector('.thumbnail-container img');
                if (posterImg && posterSrc) {
                    posterImg.src = posterSrc;
                }
                thumb.style.display = '';
            } else if (imageWrapper) {
                const newThumb = document.createElement('ed-video-thumbnail');
                newThumb.className = 'ed-video-thumbnail ed-video-thumbnail--product-card';
                newThumb.setAttribute('video-src', videoSrc);
                newThumb.setAttribute('autoplay', 'false');
                newThumb.setAttribute('controls', 'true');
                newThumb.innerHTML = `<div class="thumbnail-container btn-open">
                    <img src="${posterSrc}" alt="" width="100" loading="lazy">
                    <div class="overlay"></div>
                    <div class="play-btn"><span class="arrow-right"></span></div>
                </div>`;
                imageWrapper.appendChild(newThumb);
                this.productElements.videoThumbnail = newThumb;
            }
        } else if (thumb) {
            thumb.style.display = 'none';
        }
    }

    salePercent(priceText, compareText) {
        const price = parseFloat(String(priceText || '').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        const compare = parseFloat(String(compareText || '').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        if (compare > price && price > 0) return Math.round(((compare - price) / compare) * 100);
        return 0;
    }

    getPriceHTML(data) {
        const price = data.productPrice || '';
        const compare = data.comparePrice || '';
        const percent = this.salePercent(price, compare);
        const appSale = this.productItem.closest('.product-facet--app-sale-block');

        if (compare && percent) {
            const salePct = appSale
                ? `<span class="product-item__sale-pct" aria-hidden="true">-${percent}%</span>`
                : '';
            return `
                <span class="price price--highlight">${salePct}${price}</span>
                <span class="price price--compare">${compare}</span>
            `.trim();
        }

        return `<span class="price">${price}</span>`;
    }
}

const initializeAllSwatches = () => {
    document.querySelectorAll('.collection-color-swatch-container:not([data-swatches-initialized])')
        .forEach(container => {
            try {
                new CollectionColorSwatches(container);
                container.setAttribute('data-swatches-initialized', 'true');
            } catch (error) {
                console.error('Error initializing swatches:', error);
            }
        });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllSwatches);
} else {
    initializeAllSwatches();
}

document.addEventListener('shopify:section:load', initializeAllSwatches);

/* Dynamisch nachgeladene Produktkarten (Filter, Pagination, Empfehlungen) */
new MutationObserver((mutations) => {
    for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
            if (node.nodeType === 1 && (node.matches?.('.collection-color-swatch-container') || node.querySelector?.('.collection-color-swatch-container'))) {
                requestAnimationFrame(initializeAllSwatches);
                return;
            }
        }
    }
}).observe(document.body, { childList: true, subtree: true });
